'use client';

import { Button, Portal } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { useBodyScrollLock } from '@common/hooks';
import { useCallback, useEffect, useState } from 'react';
import CountUp from 'react-countup';

import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

export type ModalType = 'win' | 'not-eligible' | 'already-participated';

interface RandomTicketModalProps {
  isOpen: boolean;
  onClose?: () => void;
  type: ModalType;
  point?: number;
  onPrimaryClick?: () => void;
}

const CONFETTI_DELAY_MS = 1000;
const COUNT_UP_DURATION = 1;
const MODAL_BG_COLOR = '#e4302c';

interface ModalConfigItem {
  title: string;
  description: readonly [string, string];
  primaryButtonText: string;
  showTicket: boolean;
  showConfetti: boolean;
}

const MODAL_CONFIG: Record<ModalType, ModalConfigItem> = {
  win: {
    title: '랜덤 웰포인트 당첨!',
    description: ['웰포인트가', '지급되었습니다.'],
    primaryButtonText: '웰포인트 상점 구경하기',
    showTicket: true,
    showConfetti: true,
  },
  'not-eligible': {
    title: '기부하고 이벤트 참여하기',
    description: ['오늘의 지역에 기부하신 후', '참여해주세요!'],
    primaryButtonText: '기부하러 가기',
    showTicket: false,
    showConfetti: false,
  },
  'already-participated': {
    title: '랜덤 웰포인트 지급 완료!',
    description: ['12월 이벤트에 참여해주셔서', '감사합니다.'],
    primaryButtonText: '친구에게 이벤트 공유하기',
    showTicket: false,
    showConfetti: false,
  },
};

const formatNumber = (n: number) => n.toLocaleString('ko-KR');
const getPortalContainer = () => document.body;

const AnimatedPoint = ({ value }: { value: number }) => (
  <span className="tabular-nums">
    <CountUp
      start={0}
      end={value}
      duration={COUNT_UP_DURATION}
      separator=","
      formattingFn={formatNumber}
    />
  </span>
);

export const RandomTicketModal = ({
  isOpen,
  onClose,
  type,
  point = 0,
  onPrimaryClick,
}: RandomTicketModalProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const config = MODAL_CONFIG[type];

  useBodyScrollLock(isOpen);

  // ESC 키 핸들링
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // 콘페티 딜레이
  useEffect(() => {
    if (!isOpen || !config.showConfetti) {
      setShowConfetti(false);

      return;
    }

    const timer = setTimeout(() => setShowConfetti(true), CONFETTI_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isOpen, config.showConfetti]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <Portal renderTo={getPortalContainer}>
      <div className="fixed inset-0 z-20 flex-center animate-in fade-in">
        <div
          className="absolute inset-0 z-[-1] bg-Gray-black/70"
          onClick={handleBackdropClick}
        />

        {showConfetti && (
          <Lottie
            className="pointer-events-none absolute inset-0 z-10 size-full"
            loop={false}
            src="/assets/jsons/flex-confetti.json"
          />
        )}

        <article
          role="dialog"
          aria-modal="true"
          className="relative mx-20 flex w-280 flex-col items-center overflow-visible rounded-medium px-20 pb-24 pt-40"
          style={{ backgroundColor: MODAL_BG_COLOR }}
        >
          <header className="flex w-full flex-col items-center gap-8">
            <h2 className="text-center font-700 text-Gray-black typo-h5">
              {config.title}
            </h2>
            <div className="text-center font-400 text-Gray-white typo-body3">
              {type === 'win' ? (
                <>
                  <p>
                    <AnimatedPoint value={point} /> 웰포인트가
                  </p>
                  <p>지급되었습니다.</p>
                </>
              ) : (
                <>
                  <p>{config.description[0]}</p>
                  <p>{config.description[1]}</p>
                </>
              )}
            </div>
          </header>

          {config.showTicket ? (
            <div className="relative h-136 w-full">
              <Image
                src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/coupon-modal.png`}
                alt="당첨 웰포인트"
                fill
                className="object-contain"
              />
              <div className="absolute inset-0 pt-30 flex-center">
                <p className="font-700 tabular-nums text-Gray-black typo-body1">
                  <AnimatedPoint value={point} /> 웰포인트
                </p>
              </div>
            </div>
          ) : (
            <div className="pointer-events-none relative z-10 -mb-16 h-160 w-240">
              <Image
                src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/santa-modal.png`}
                alt="산타"
                fill
                className="object-contain"
              />
            </div>
          )}

          <footer className="flex w-full flex-col items-center gap-2">
            <Button className="w-full" onClick={onPrimaryClick}>
              {config.primaryButtonText}
            </Button>
            <button
              type="button"
              className="h-48 w-full font-700 text-Gray-white typo-body3"
              onClick={onClose}
            >
              닫기
            </button>
          </footer>
        </article>
      </div>
    </Portal>
  );
};
